import { zodResolver } from "@hookform/resolvers/zod";
import { ERROR_CODES } from "@mozioinc/query";
import { Loading, Container } from "@mozioinc/ui";
import { captureException } from "@sentry/react";
import { useStripe, useElements, Elements } from "@stripe/react-stripe-js";
import {
  queryOptions,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useSearch, useNavigate } from "@tanstack/react-router";
import posthog from "posthog-js";
import { useState, useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Dialog, BookingForm, ErrorDialog } from "../components";
import { formSchema } from "../components/booking-form";
import { parkingQuery } from "../utils/query";
import { getStripePromise } from "../utils/stripe";
import { ReactComponent as ProcessingPaymentSvg } from "./assets/processing-payment.svg";
import type { FormValues } from "../components/booking-form";
import type { TransNamespace } from "../types";
import type { ParkingSearch, ParkingPricingResult } from "@mozioinc/query";

export const searchParamsSchema = z.object({
  searchId: z.string(),
  resultId: z.string(),
  providerId: z.string(),
});

type SearchResultQueryProps = z.infer<typeof searchParamsSchema>;

export const searchResultQueryOptions = ({
  providerId,
  resultId,
  searchId,
}: SearchResultQueryProps) =>
  queryOptions({
    queryKey: ["searchResult", searchId, resultId, providerId],
    queryFn: () =>
      parkingQuery.getSearchResult({
        searchId,
        resultId,
        providerId,
      }),
  });

const useSearchResult = () => {
  const search = useSearch({ from: "/booking" });
  const searchResultQuery = useSuspenseQuery(searchResultQueryOptions(search));

  return {
    error:
      searchResultQuery.data && "error" in searchResultQuery.data
        ? searchResultQuery.data
        : null,
    data:
      searchResultQuery.data && "searchId" in searchResultQuery.data
        ? searchResultQuery.data
        : null,
  };
};

export const useUpdateSearchResult = () => {
  const navigate = useNavigate({ from: "/" });
  const { providerId, resultId, searchId } = useSearch({ from: "/booking" });

  const updateSearchMutation = useMutation({
    mutationFn: () =>
      parkingQuery.updateSearchResult({
        searchId,
        resultId,
        providerId,
      }),
  });

  if (updateSearchMutation.error) {
    throw updateSearchMutation.error;
  }

  const onSearchResultUpdate = async () => {
    const data = await updateSearchMutation.mutateAsync();

    if ("searchId" in data) {
      const { searchId, results } = data;

      await navigate({
        search: (previous) => ({
          ...previous,
          searchId,
          resultId: results[0]?.resultId ?? resultId,
        }),
      });

      window.location.reload();
    }
  };

  return {
    error:
      updateSearchMutation.data && "error" in updateSearchMutation.data
        ? updateSearchMutation.data
        : null,
    isLoading: updateSearchMutation.isPending,
    onSearchResultUpdate,
  };
};

type CalculatePricingProps = {
  couponCode: string;
};

const useCalculatePricing = ({ couponCode }: CalculatePricingProps) => {
  const { providerId, resultId, searchId } = useSearch({ from: "/booking" });
  const paymentIntentIdRef = useRef("");
  const clientSecretRef = useRef("");

  const {
    data: result,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["calculatePricing", couponCode, searchId, resultId, providerId],
    queryFn: () =>
      parkingQuery.calculatePricing({
        searchId,
        resultId,
        providerId,
        couponCode,
        paymentIntentId: paymentIntentIdRef.current,
      }),
    gcTime: 0,
  });

  const data = result && "currency" in result ? result : null;
  const paymentIntentId = data ? data.paymentIntentId : "";
  const clientSecret = data ? data.clientSecret : "";

  if (
    paymentIntentId &&
    clientSecret &&
    paymentIntentId !== paymentIntentIdRef.current
  ) {
    paymentIntentIdRef.current = paymentIntentId;
    clientSecretRef.current = clientSecret;
  }

  if (error) {
    throw error;
  }

  return {
    error: result && "error" in result ? result : null,
    loading: isLoading,
    data,
    paymentIntentId: paymentIntentIdRef.current,
    clientSecret: clientSecretRef.current,
  };
};

const useCreateReservation = () => {
  const navigate = useNavigate();

  const createReservationMutation = useMutation({
    mutationFn: parkingQuery.createReservation,
    onSuccess: (data) => {
      if ("id" in data && data.id) {
        navigate({
          to: "/confirmation",
          search: {
            reservationId: data.id,
          },
        });
      }
    },
  });

  if (createReservationMutation.error) {
    throw createReservationMutation.error;
  }

  return {
    error:
      createReservationMutation.data &&
      "error" in createReservationMutation.data
        ? createReservationMutation.data
        : null,
    createReservationMutate: createReservationMutation.mutate,
    loading: createReservationMutation.isPending,
  };
};

const Booking = () => {
  const { t } = useTranslation<TransNamespace>("parking:booking");
  const [couponCode, setCouponCode] = useState("");

  const { data: searchResultData, error: searchResultError } =
    useSearchResult();

  const {
    onSearchResultUpdate,
    error: updateSearchResultError,
    isLoading: updateSearchResultIsLoading,
  } = useUpdateSearchResult();

  const {
    data: calculatePricingData,
    error: calculatePricingError,
    loading: calculatePricingIsLoading,
    paymentIntentId,
    clientSecret,
  } = useCalculatePricing({
    couponCode,
  });

  const loading = calculatePricingIsLoading && !paymentIntentId;

  const { error, message: errorMessage } =
    calculatePricingError || searchResultError || updateSearchResultError || {};
  const searchExpired = error === ERROR_CODES.SEARCH_EXPIRED;

  return (
    <>
      {loading && <Loading className="min-h-screen" />}
      {error && (
        <ErrorDialog
          error={error}
          onClick={() => {
            if (searchExpired) {
              onSearchResultUpdate();
            } else {
              // TODO: Fow now, we'll refresh the page to get a new payment intent id, to be improved soon
              window.location.reload();
            }
          }}
          title={
            searchExpired ? t("Prices need to be updated!") : t("Payment error")
          }
          loading={updateSearchResultIsLoading}
          message={errorMessage}
        />
      )}
      {!error && !loading && (
        <>
          <div className="bg-primary px-10 py-2 text-center">
            <h1 className="text-primary-contrast text-2xl font-bold lg:py-6 lg:text-4xl lg:leading-normal">
              {t("Almost done booking")}
            </h1>
          </div>
          <Container className="p-0 md:px-0 lg:px-4">
            {clientSecret && searchResultData && (
              <Elements options={{ clientSecret }} stripe={getStripePromise()}>
                <BookingFormProvider
                  searchResult={searchResultData}
                  pricingResult={calculatePricingData}
                  onCouponCodeChange={setCouponCode}
                  paymentIntentId={paymentIntentId}
                />
              </Elements>
            )}
          </Container>
        </>
      )}
    </>
  );
};

type BookingFormProviderProps = {
  searchResult: ParkingSearch;
  pricingResult: ParkingPricingResult | null;
  onCouponCodeChange: (coupon: string) => void;
  paymentIntentId?: string;
};

const BookingFormProvider = ({
  searchResult,
  pricingResult,
  onCouponCodeChange,
  paymentIntentId = "",
}: BookingFormProviderProps) => {
  const { t } = useTranslation<TransNamespace>("parking:booking");
  const [showProcessingPaymentDialog, setShowProcessingPaymentDialog] =
    useState(false);
  const stripe = useStripe() ?? undefined;
  const elements = useElements();
  const { providerId, resultId, searchId } = useSearch({ from: "/booking" });

  const { onSearchResultUpdate, isLoading: updateSearchResultIsLoading } =
    useUpdateSearchResult();

  const {
    createReservationMutate,
    loading: createReservationIsLoading,
    error: createReservationError,
  } = useCreateReservation();

  const confirmPaymentMutation = useMutation({
    mutationFn: () => {
      if (!stripe || !elements) {
        throw new Error("Stripe.js has not loaded yet");
      }

      return stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });
    },
    onMutate: () => {
      setShowProcessingPaymentDialog(true);
    },
    onSettled: () => {
      // TODO: Validate with designer best approach for this dialog
      setTimeout(() => {
        setShowProcessingPaymentDialog(false);
      }, 1000);
    },
    onSuccess: (data) => {
      // TODO: reservation should not be created from front-end. if anything happens here, we'll charge the customer and not create the reservation
      if (!data.paymentIntent && !data.error) {
        console.error("Payment intent not found. This should not happen.");
        return;
      }

      if (data.paymentIntent) {
        handleCreateReservation(paymentIntentId);
      }
    },
  });

  const { handleSubmit, getValues, ...methods } = useForm<FormValues>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    shouldFocusError: true,
  });

  const handleCreateReservation = (paymentIntentId?: string) => {
    const { customer, departureFlight, arrivalFlight, ...formData } =
      getValues();

    if (searchResult) {
      createReservationMutate({
        ...formData,
        customer: {
          ...customer,
          phone: customer.countryCode.callingCode + customer.phone,
        },
        departureFlight,
        arrivalFlight,
        searchId,
        resultId,
        providerId,
        paymentIntentId,
        searchContextHash: searchResult.searchContextHash,
      });
    }
  };

  const submit = () => {
    if (!pricingResult) {
      console.error("Pricing result is not available, this should not happen.");
      captureException(
        "Pricing result is not available, this should not happen.",
      );
      return;
    }

    posthog.capture("Parking: Checkout Form Submitted", {
      price_amount: pricingResult?.originalPrice.value,
      price_currency: pricingResult?.currency,
      airport_transfer_type:
        searchResult?.results[0]?.airportTransferSummary.type,
      provider_id: providerId,
      location_name: searchResult?.location.name,
      location_value: searchResult?.location.value,
    });

    const requirePayment = pricingResult.finalPrice.smallestCurrencyUnit > 0;
    if (requirePayment) {
      confirmPaymentMutation.mutate();
    } else {
      handleCreateReservation();
    }
  };

  const stripeError =
    confirmPaymentMutation.data?.error &&
    confirmPaymentMutation.data?.error?.message
      ? ERROR_CODES.PAYMENT_ERROR
      : undefined;
  const { error, message: errorMessage } = createReservationError || {};
  const searchExpired = error === ERROR_CODES.SEARCH_EXPIRED;

  return (
    <>
      {error && (
        <ErrorDialog
          error={error}
          onClick={() => {
            if (searchExpired) {
              onSearchResultUpdate();
            } else {
              // TODO: Fow now, we'll refresh the page to get a new payment intent id, to be improved soon
              window.location.reload();
            }
          }}
          title={
            searchExpired ? t("Prices need to be updated!") : t("Payment error")
          }
          loading={updateSearchResultIsLoading}
          message={errorMessage}
        />
      )}
      {stripeError && (
        <ErrorDialog
          title={t("Payment error")}
          error={stripeError}
          onClick={confirmPaymentMutation.reset}
          loading={updateSearchResultIsLoading}
          message={confirmPaymentMutation.data?.error?.message}
        />
      )}

      <FormProvider
        handleSubmit={handleSubmit}
        getValues={getValues}
        {...methods}
      >
        {!createReservationError &&
          (showProcessingPaymentDialog || createReservationIsLoading) && (
            <Dialog
              open={true}
              icon={<ProcessingPaymentSvg className="text-primary text-6xl" />}
            >
              <h2 className="mb-1 mt-7 text-2xl font-bold leading-9">
                {t("Processing payment")}
              </h2>
              <p className="mb-8">{t("Please don't close this window.")}</p>
            </Dialog>
          )}
        <BookingForm
          searchResult={searchResult}
          pricingResult={pricingResult}
          onSubmit={handleSubmit(submit)}
          onCouponCodeChange={onCouponCodeChange}
          loading={showProcessingPaymentDialog || createReservationIsLoading}
        />
      </FormProvider>
    </>
  );
};

export { Booking };
